import { createSlice } from "@reduxjs/toolkit";
import {
  downloadPaymentMethodsV3,
  getPaymentMethodsV3Daily,
  getPaymentMethodsV3Details,
  getPaymentMethodsV3Insights,
  getPaymentMethodsV3InsightsBreakdowns,
  getPaymentMethodsV3InsightsCustomers,
  getPaymentMethodsV3InsightsDaily,
  getPaymentMethodsV3Ranking,
  getPaymentMethodsV3Share,
  getPaymentMethodsV3Summary,
} from "./paymentMethodsV3Action";

const section = (data) => ({
  loading: false,
  error: null,
  data,
  latestRequestId: null,
});

// The insights answers do not name their payment type: the bucket keeps the
// one it asked for.
const insightsSection = (data) => ({ ...section(data), paymentType: null });

const initialState = {
  ranking: section({ rows: [], totals: {} }),
  share: section({ rows: [], total: {} }),
  summary: section(null),
  daily: section({ results: [], previous: null }),
  details: section({ rows: [], meta: null }),
  insights: insightsSection(null),
  insightsDaily: insightsSection({ results: [], previous: null }),
  insightsBreakdowns: insightsSection(null),
  insightsCustomers: insightsSection({ top_customers: [] }),
  download: { loading: false, error: null },
};

// Filters change faster than the sections answer: only the latest request of
// a section may write to it.
const addSectionCases = (builder, thunk, key) =>
  builder
    .addCase(thunk.pending, (state, action) => {
      state[key].loading = true;
      state[key].error = null;
      state[key].latestRequestId = action.meta.requestId;
      if ("paymentType" in state[key]) {
        const next = action.meta.arg?.payment_type ?? null;
        if (state[key].paymentType !== next) {
          state[key].data = initialState[key].data;
        }
        state[key].paymentType = next;
      }
    })
    .addCase(thunk.fulfilled, (state, action) => {
      if (action.meta.requestId !== state[key].latestRequestId) return;
      state[key].loading = false;
      state[key].data = action.payload;
    })
    .addCase(thunk.rejected, (state, action) => {
      if (action.meta.requestId !== state[key].latestRequestId) return;
      state[key].loading = false;
      state[key].data = initialState[key].data;
      state[key].error = action.payload;
    });

const paymentMethodsV3 = createSlice({
  name: "paymentMethodsV3",
  initialState,
  reducers: {},
  extraReducers: (builder) => {
    addSectionCases(builder, getPaymentMethodsV3Ranking, "ranking");
    addSectionCases(builder, getPaymentMethodsV3Share, "share");
    addSectionCases(builder, getPaymentMethodsV3Summary, "summary");
    addSectionCases(builder, getPaymentMethodsV3Daily, "daily");
    addSectionCases(builder, getPaymentMethodsV3Details, "details");
    addSectionCases(builder, getPaymentMethodsV3Insights, "insights");
    addSectionCases(builder, getPaymentMethodsV3InsightsDaily, "insightsDaily");
    addSectionCases(
      builder,
      getPaymentMethodsV3InsightsBreakdowns,
      "insightsBreakdowns"
    );
    addSectionCases(
      builder,
      getPaymentMethodsV3InsightsCustomers,
      "insightsCustomers"
    );
    builder
      .addCase(downloadPaymentMethodsV3.pending, (state) => {
        state.download.loading = true;
        state.download.error = null;
      })
      .addCase(downloadPaymentMethodsV3.fulfilled, (state) => {
        state.download.loading = false;
      })
      .addCase(downloadPaymentMethodsV3.rejected, (state, action) => {
        state.download.loading = false;
        state.download.error = action.payload;
      });
  },
});

export default paymentMethodsV3.reducer;
